import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BuyGuideToken } from '@sleed/bizcommerce-api';
import { buyGuidesApi, binaryRepoImagesApi } from '~/lib/api/client';
import { formToToken, type BuyGuideFormValues } from '~/lib/schemas/buy-guide';
import { categoryKeys } from '~/hooks/use-categories';

export const buyGuideKeys = {
	all: ['buy-guides'] as const,
	list: () => [...buyGuideKeys.all, 'list'] as const,
	detail: (id: number | string) => [...buyGuideKeys.all, 'detail', id] as const
};

/** All buy guides (incl. each guide's `catlst` category ids). Server role: Catalog. */
export function useBuyGuidesList(enabled = true) {
	return useQuery<BuyGuideToken[]>({
		queryKey: buyGuideKeys.list(),
		queryFn: async () => (await buyGuidesApi.getAll()).data.items ?? [],
		enabled
	});
}

/** Single buy guide by id (`create` → null). GetById does not include `catlst` — read it from the list. */
export function useBuyGuide(id: string, enabled = true) {
	const numericId = Number(id);
	const valid = Number.isFinite(numericId) && numericId > 0;
	return useQuery<BuyGuideToken | null>({
		queryKey: buyGuideKeys.detail(id),
		queryFn: async () => (valid ? (await buyGuidesApi.getById(numericId)).data : null),
		enabled: enabled && valid
	});
}

/**
 * Upload the buy-guide image (slot "1"). The guide must already exist (id > 0); the generated client
 * dropped the multipart param, so the File goes in as form-data keyed by the slot subkey (same
 * contract as the manufacturer upload, BinaryRepoImages/UploadBuyGuide).
 */
export async function uploadBuyGuideImage(buyGuideId: number, file: File): Promise<void> {
	const fd = new FormData();
	fd.append('1', file);
	await binaryRepoImagesApi.uploadBuyGuide(buyGuideId, '1', { data: fd });
}

export function useBuyGuideMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: buyGuideKeys.all });
	const create = useMutation({
		mutationFn: async (form: BuyGuideFormValues) => (await buyGuidesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: BuyGuideFormValues) => (await buyGuidesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({
		mutationFn: async (id: number) => (await buyGuidesApi.remove(id)).data,
		onSuccess: () => {
			void invalidate();
			// Category editors cache BuyGuideId lookups; refresh them after a delete.
			void qc.invalidateQueries({ queryKey: categoryKeys.all });
		}
	});
	/** Replace the set of categories that show this guide (server unassigns the rest). */
	const setCategories = useMutation({
		mutationFn: async ({ buyGuideId, catlst }: { buyGuideId: number; catlst: number[] }) =>
			(await buyGuidesApi.massUpdateCategories({ buyGuideId, catlst })).data,
		onSuccess: () => {
			void invalidate();
			void qc.invalidateQueries({ queryKey: categoryKeys.all });
		}
	});
	return { create, update, remove, setCategories };
}
